import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useTranslation } from '@/contexts/LanguageContext';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';

export default function ForgotPasswordPage() {
  const { t } = useTranslation('forgotPassword');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      setSent(true);
    } catch {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        {sent ? (
          <div className="bg-white dark:bg-neutral-800 border dark:border-neutral-700 rounded-xl p-8 text-center" data-testid="forgot-password-sent">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 mb-4">
              <CheckCircle className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="font-heading text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">{t('sentTitle')}</h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">{t('sentText').replace('{email}', email)}</p>
            <Link to="/connexion" className="text-sm font-medium text-[#1F3F72] dark:text-[#F25C05] hover:underline">
              <ArrowLeft className="w-3 h-3 inline mr-1" /> {t('backToLogin')}
            </Link>
          </div>
        ) : (
          <div className="bg-white dark:bg-neutral-800 border dark:border-neutral-700 rounded-xl p-8">
            <h1 className="font-heading text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">{t('title')}</h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">{t('subtitle')}</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>{t('emailLabel')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('emailPlaceholder')}
                    className="pl-9"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#1F3F72] text-white" disabled={loading} data-testid="forgot-submit-btn">
                {loading ? t('sending') : t('submitBtn')}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Link to="/connexion" className="text-sm text-neutral-500 hover:text-[#1F3F72] dark:hover:text-[#F25C05]">
                <ArrowLeft className="w-3 h-3 inline mr-1" /> {t('backToLogin')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
